import { type Ref, useEffect, useState } from 'react';

import { Loader2 } from 'lucide-react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

import { cn } from '@/lib/utils';

import { Hint } from './Hint';

/** Footer notice when an editor's saved answers failed to load. */
export const loadErrorMessage = (what: string) =>
    `Couldn't load the saved ${what}. Save is off so they aren't overwritten.`;

/**
 * Shared save footer + validation-errors list for the three GT layer editors,
 * extracted to avoid duplication across PageSplitEditor, SheetSplitEditor, and RowEditor.
 */
export function EditorFooter({
    errors,
    onSave,
    isPending,
    saveLabel,
    testId,
    disabled = false,
    loadError = null,
    clearCount = 0,
    clearNoun,
    conflict = null,
    onReload,
    leading,
    step = null,
    primaryRef,
    hideErrorList = false,
    primaryClassName,
    inline = false,
}: {
    errors: string[];
    onSave: (options?: { confirmClear?: boolean }) => void;
    isPending: boolean;
    saveLabel: string;
    testId?: string;
    /** Blocks Save without a message, e.g. while the saved answers are still loading. */
    disabled?: boolean;
    /** Why the saved answers could not be loaded; shown above a disabled Save so an empty grid can't overwrite them. */
    loadError?: string | null;
    /** Saved answers this save would delete; above 0, Save asks before clearing them. */
    clearCount?: number;
    /** Singular name of what gets cleared, e.g. "page split". */
    clearNoun?: string;
    /** Why the last save was refused because someone else saved first; blocks Save until Reload. */
    conflict?: string | null;
    /** Loads the other person's saved answers in place of the unsaved edits. */
    onReload?: () => void;
    /** Content placed before Save on one line, e.g. a confirmation checkbox. */
    leading?: React.ReactNode;
    /** Replaces Save as the one primary button once there is nothing to save, e.g. Create documents or Next. */
    step?: {
        label: string;
        onClick: () => void;
        testId?: string;
        disabled?: boolean;
        /** Hover text explaining what the button does. */
        title?: string;
        className?: string;
    } | null;
    /** The primary button, for a keyboard shortcut that presses it. */
    primaryRef?: Ref<HTMLButtonElement>;
    /** Keeps the errors blocking Save but leaves listing them to the caller. */
    hideErrorList?: boolean;
    /** Extra classes for the Save button. */
    primaryClassName?: string;
    /** Renders inside a scrolling list instead of as a pinned, bordered footer. */
    inline?: boolean;
}) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const blocked =
        isPending ||
        errors.length > 0 ||
        disabled ||
        loadError !== null ||
        conflict !== null;
    const canConfirmClear = clearCount > 0 && !blocked;
    // Closes the confirm once it no longer applies, e.g. the editor moved to a record still loading.
    useEffect(() => {
        if (!canConfirmClear) setConfirmOpen(false);
    }, [canConfirmClear]);
    const savedAnswers =
        clearCount === 1
            ? `the 1 saved ${clearNoun}`
            : `all ${clearCount} saved ${clearNoun}s`;
    const handleSave = () => (clearCount > 0 ? setConfirmOpen(true) : onSave());

    return (
        <>
            {errors.length > 0 && !hideErrorList && (
                // Capped and scrolled so a long list cannot squeeze out the rows and save button.
                <ul
                    className={cn(
                        'max-h-32 shrink-0 overflow-y-auto space-y-1',
                        inline ? 'pt-1' : 'px-4 pt-3',
                    )}
                    tabIndex={0}
                    aria-label="Validation errors"
                    data-testid="EditorFooter__errors"
                >
                    {errors.map((e) => (
                        <li key={e} className="text-xs text-destructive">
                            {e}
                        </li>
                    ))}
                </ul>
            )}

            <div
                className={cn(
                    inline
                        ? 'flex flex-col gap-2.5 rounded-lg border border-[#E2F0F0] bg-white p-3'
                        : 'px-4 py-3 border-t shrink-0',
                )}
            >
                {loadError && (
                    <p
                        role="alert"
                        className="mb-2 text-xs text-destructive"
                        data-testid="EditorFooter__loadError"
                    >
                        {loadError}
                    </p>
                )}
                {conflict && (
                    <div
                        role="alert"
                        className="mb-2 flex items-center gap-2 text-xs text-destructive"
                        data-testid="EditorFooter__conflict"
                    >
                        <p className="flex-1">{conflict}</p>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={onReload}
                            data-testid="EditorFooter__reloadButton"
                        >
                            Reload
                        </Button>
                    </div>
                )}
                <div
                    className={
                        leading && !inline
                            ? 'flex flex-wrap items-center justify-between gap-3'
                            : inline
                              ? 'flex flex-col gap-2.5'
                              : undefined
                    }
                >
                    {leading}
                    {step ? (
                        <Hint
                            text={step.title}
                            focusable={step.disabled}
                            className={leading && !inline ? 'inline-flex shrink-0' : 'flex w-full'}
                        >
                            <Button
                                ref={primaryRef}
                                type="button"
                                className={cn(
                                    leading && !inline ? 'shrink-0' : 'w-full',
                                    inline && 'h-9',
                                    step.className,
                                )}
                                disabled={step.disabled}
                                onClick={step.onClick}
                                data-testid={step.testId}
                            >
                                {step.label}
                            </Button>
                        </Hint>
                    ) : (
                        <Button
                            ref={primaryRef}
                            type="button"
                            className={cn(
                                leading && !inline ? 'shrink-0' : 'w-full',
                                inline && 'h-9',
                                primaryClassName,
                            )}
                            disabled={blocked}
                            onClick={handleSave}
                            data-testid={testId}
                        >
                            {isPending && (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            )}
                            {isPending ? 'Saving…' : saveLabel}
                        </Button>
                    )}
                </div>
            </div>

            <AlertDialog
                open={confirmOpen && canConfirmClear}
                onOpenChange={setConfirmOpen}
            >
                <AlertDialogContent data-testid="EditorFooter__clearDialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {clearCount === 1
                                ? `Delete the saved ${clearNoun}?`
                                : `Delete all saved ${clearNoun}s?`}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            The grid is empty, so saving deletes {savedAnswers}.
                            This can&apos;t be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="EditorFooter__clearCancelButton">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() =>
                                canConfirmClear &&
                                onSave({ confirmClear: true })
                            }
                            data-testid="EditorFooter__clearConfirmButton"
                        >
                            Delete all
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
